/* eslint-disable max-len, no-nested-ternary */
import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Card, Form, Button, InputGroup } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isNil, isEmpty } from 'lodash';

import * as templateOps from '../../../api/AnnotationTemplates/annotation-template-operations';
import { ELEMENT_TYPES } from '../../../api/AnnotationTemplates/AnnotationTemplates';

import ColBreak from '../../atoms/ColBreak';
import ResponsivePlayer from '../ResponsivePlayer';

import Styles from './styles';

import TemplateItemImaps from './TemplateImapsItem/TemplateItemImaps';
import TemplatePointCloudItem from './TemplatePointCloudItem/TemplatePointCloudItem.js';

import OpenModalFromUrl from '../OpenModalFromUrl';

import TemplateButtonItem from './TemplateButtonItem/index.js';
import TemplateItemMultipleImaps from './TemplateMultipleImapsItem/index.js';

const EDITION_MODE = 'edition';
const PREVIEW_MODE = 'preview';

const IframeItem = ({ elementItem, updateAnnotationFields, previewMode }) => {
  if (previewMode === PREVIEW_MODE && !isEmpty(templateOps.getElementValue(elementItem)))
    return (
      <Form.Group className="mb-0">
        <Form.Label>{templateOps.getElementCaption(elementItem)}</Form.Label>
        <Styles.Iframe
          src={templateOps.getElementValue(elementItem)}
          height={templateOps.getElementHeight(elementItem)}
          width="100%"
          title="External Page"
        />
      </Form.Group>
    );

  if (previewMode === EDITION_MODE)
    return (
      <Form.Group className="mb-0">
        <Form.Label>Iframe</Form.Label>
        <Form.Control
          type="text"
          name={`${templateOps.getElementKey(elementItem)}-url`}
          placeholder="URL for the iframe"
          defaultValue={templateOps.getElementValue(elementItem)}
          onBlur={event => updateAnnotationFields(elementItem, 'value', event.target.value)}
        />
        <Form.Control
          type="text"
          name={`${templateOps.getElementKey(elementItem)}-caption`}
          placeholder="Caption for the iframe"
          defaultValue={templateOps.getElementCaption(elementItem)}
          onBlur={event => updateAnnotationFields(elementItem, 'caption', event.target.value)}
        />
        <InputGroup>
          <InputGroup.Prepend>
            <InputGroup.Text>Height</InputGroup.Text>
          </InputGroup.Prepend>
          <Form.Control
            type="number"
            name={`${templateOps.getElementKey(elementItem)}-height`}
            placeholder="Height for the iframe (px)"
            defaultValue={templateOps.getElementHeight(elementItem)}
            onBlur={event =>
              updateAnnotationFields(elementItem, 'height', Number(event.target.value))
            }
          />
          <InputGroup.Append>
            <InputGroup.Text>px</InputGroup.Text>
          </InputGroup.Append>
        </InputGroup>
      </Form.Group>
    );

  return '';
};

IframeItem.defaultProps = {
  updateAnnotationFields: () => console.error('no action to update')
};

IframeItem.propTypes = {
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func,
  previewMode: PropTypes.string.isRequired
};

const ImapsItem = ({ elementItem, updateAnnotationFields, previewMode, ...rest }) => {
  return (
    <TemplateItemImaps
      elementItem={elementItem}
      updateAnnotationFields={updateAnnotationFields}
      previewMode={previewMode}
      {...rest}
    />
  );
};
ImapsItem.defaultProps = {
  previewMode: 'preview',
  updateAnnotationFields: () => console.error('no action to update')
};

ImapsItem.propTypes = {
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func,
  previewMode: PropTypes.string
};

const MultipleMapsItem = ({ elementItem, updateAnnotationFields, previewMode, ...rest }) => {
  return (
    <TemplateItemMultipleImaps
      elementItem={elementItem}
      updateAnnotationFields={updateAnnotationFields}
      previewMode={previewMode}
      {...rest}
    />
  );
};
MultipleMapsItem.defaultProps = {
  previewMode: 'preview',
  updateAnnotationFields: () => console.error('no action to update')
};

MultipleMapsItem.propTypes = {
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func,
  previewMode: PropTypes.string
};

const PointCloudItem = ({ elementItem, updateAnnotationFields, previewMode, ...rest }) => {
  return (
    <TemplatePointCloudItem
      elementItem={elementItem}
      updateAnnotationFields={updateAnnotationFields}
      previewMode={previewMode}
      {...rest}
    />
  );
};
PointCloudItem.defaultProps = {
  previewMode: 'preview',
  updateAnnotationFields: () => console.error('no action to update')
};

PointCloudItem.propTypes = {
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func,
  previewMode: PropTypes.string
};

const ButtonItem = ({ elementItem, updateAnnotationFields, previewMode, ...rest }) => {
  return (
    <TemplateButtonItem
      elementItem={elementItem}
      updateAnnotationFields={updateAnnotationFields}
      previewMode={previewMode}
      {...rest}
    />
  );
};
ButtonItem.defaultProps = {
  previewMode: 'preview',
  updateAnnotationFields: () => console.error('no action to update')
};

ButtonItem.propTypes = {
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func,
  previewMode: PropTypes.string
};

const TextAreaItem = ({ elementItem, updateAnnotationFields, previewMode }) => {
  if (previewMode === PREVIEW_MODE && !isEmpty(templateOps.getElementValue(elementItem)))
    return (
      <Form.Group className="mb-0">
        <Form.Label>{templateOps.getElementLabelName(elementItem)}</Form.Label>
        <Styles.Content>
          <ReactMarkdown source={templateOps.getElementValue(elementItem)} />
        </Styles.Content>
      </Form.Group>
    );

  if (previewMode === EDITION_MODE)
    return (
      <Form.Group className="mb-0">
        <Form.Label>{templateOps.getElementLabelName(elementItem)}</Form.Label>
        <Form.Control
          as="textarea"
          name={`${templateOps.getElementKey(elementItem)}-textAreaKey`}
          placeholder="Write your text here"
          defaultValue={templateOps.getElementValue(elementItem)}
          onBlur={event => updateAnnotationFields(elementItem, 'value', event.target.value)}
        />
      </Form.Group>
    );

  return '';
};

TextAreaItem.defaultProps = {
  updateAnnotationFields: () => console.error('no action to update')
};

TextAreaItem.propTypes = {
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func,
  previewMode: PropTypes.string.isRequired
};

const IconItem = ({ elementItem, updateAnnotationFields, previewMode }) => {
  if (previewMode === PREVIEW_MODE && !isEmpty(templateOps.getElementValue(elementItem)))
    return (
      <Form.Group className="mb-0" key={templateOps.getElementKey(elementItem)}>
        <Button
          variant="link"
          className="px-0 text-left text-break d-flex flex-row align-items-center"
          href={templateOps.getElementValue(elementItem)}
          target="_blank"
          rel="noreferrer noopener"
        >
          <FontAwesomeIcon icon={templateOps.getElementIconKey(elementItem)} size="lg" />
          <span className="ml-1">{templateOps.getElementCaption(elementItem)}</span>
        </Button>
      </Form.Group>
    );

  if (previewMode === EDITION_MODE)
    return (
      <Form.Group className="mb-0">
        <Form.Label>Icon</Form.Label>
        <Form.Control
          type="text"
          name={`${templateOps.getElementKey(elementItem)}-iconKey`}
          placeholder="Fontawesome Icon Key"
          defaultValue={templateOps.getElementIconKey(elementItem)}
          onBlur={event => updateAnnotationFields(elementItem, 'iconKey', event.target.value)}
        />
        <Form.Control
          type="text"
          name={`${templateOps.getElementKey(elementItem)}-caption`}
          placeholder="Caption for the icon link"
          defaultValue={templateOps.getElementCaption(elementItem)}
          onBlur={event => updateAnnotationFields(elementItem, 'caption', event.target.value)}
        />
        <Form.Control
          type="text"
          name={`${templateOps.getElementKey(elementItem)}-url`}
          placeholder="URL for the icon link"
          defaultValue={templateOps.getElementValue(elementItem)}
          onBlur={event => updateAnnotationFields(elementItem, 'value', event.target.value)}
        />
      </Form.Group>
    );

  return '';
};

IconItem.defaultProps = {
  updateAnnotationFields: () => console.error('no action to update')
};

IconItem.propTypes = {
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func,
  previewMode: PropTypes.string.isRequired
};

const LinkItem = ({ elementItem, updateAnnotationFields, previewMode }) => {
  if (previewMode === PREVIEW_MODE && !isEmpty(templateOps.getElementValue(elementItem)))
    return (
      <Form.Group
        className="mb-0 align-items-center"
        as={Row}
        key={templateOps.getElementKey(elementItem)}
      >
        <Col xs={2}>
          <FontAwesomeIcon icon="globe-africa" />
        </Col>
        <Col xs={10}>
          <a
            href={templateOps.getElementValue(elementItem)}
            target="_blank"
            rel="noreferrer noopener"
          >
            {templateOps.getElementCaption(elementItem)}
          </a>
        </Col>
      </Form.Group>
    );

  if (previewMode === EDITION_MODE)
    return (
      <Form.Group className="mb-0">
        <Form.Label>Link</Form.Label>
        <Form.Control
          type="text"
          name={`${templateOps.getElementKey(elementItem)}-url`}
          placeholder="URL for the link"
          defaultValue={templateOps.getElementValue(elementItem)}
          onBlur={event => updateAnnotationFields(elementItem, 'value', event.target.value)}
        />
        <Form.Control
          type="text"
          name={`${templateOps.getElementKey(elementItem)}-caption`}
          placeholder="Caption for the link"
          defaultValue={templateOps.getElementCaption(elementItem)}
          onBlur={event => updateAnnotationFields(elementItem, 'caption', event.target.value)}
        />
      </Form.Group>
    );

  return '';
};

LinkItem.defaultProps = {
  updateAnnotationFields: () => console.error('no action to update')
};

LinkItem.propTypes = {
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func,
  previewMode: PropTypes.string.isRequired
};

const VideoItem = ({ elementItem, updateAnnotationFields, previewMode, isPreviewMapEditor }) => {
  const playerVars = {
    modestbranding: 1,
    rel: 0,
    showinfo: 0
  };

  if (previewMode === PREVIEW_MODE && !isEmpty(templateOps.getElementValue(elementItem)))
    return (
      <Form.Group className="mb-0">
        <Form.Label>{templateOps.getElementCaption(elementItem)}</Form.Label>
        <ResponsivePlayer
          url={templateOps.getElementValue(elementItem)}
          showControls
          playerVars={playerVars}
          isPreviewMapEditor={isPreviewMapEditor}
        />
      </Form.Group>
    );

  if (previewMode === EDITION_MODE)
    return (
      <Form.Group className="mb-0">
        <Form.Label>Video</Form.Label>
        <Form.Control
          type="text"
          name={`${templateOps.getElementKey(elementItem)}-url`}
          placeholder="URL for the Youtube Video"
          defaultValue={templateOps.getElementValue(elementItem)}
          onBlur={event => updateAnnotationFields(elementItem, 'value', event.target.value)}
        />
        <Form.Control
          type="text"
          name={`${templateOps.getElementKey(elementItem)}-caption`}
          placeholder="Caption for the video"
          defaultValue={templateOps.getElementCaption(elementItem)}
          onBlur={event => updateAnnotationFields(elementItem, 'caption', event.target.value)}
        />
      </Form.Group>
    );

  return '';
};

VideoItem.defaultProps = {
  updateAnnotationFields: () => console.error('no action to update')
};

VideoItem.propTypes = {
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func,
  previewMode: PropTypes.string.isRequired
};

const ImageItem = ({ elementItem, updateAnnotationFields, previewMode }) => {
  const [openFullScreen, setOpenFullScreen] = React.useState(false);
  if (previewMode === PREVIEW_MODE && !isEmpty(templateOps.getElementValue(elementItem)))
    return (
      <Card>
        <Button
          style={{ padding: 0, border: 0 }}
          variant="outlined"
          onClick={
            !isEmpty(templateOps.getElementHiperLink(elementItem))
              ? () => window.open(templateOps.getElementHiperLink(elementItem), '_blank')
              : () => {}
          }
        >
          <Card.Img variant="top" src={templateOps.getElementValue(elementItem)} alt="image" />
        </Button>
        {!isEmpty(templateOps.getElementCaption(elementItem)) && (
          <Card.Body>
            <Card.Text style={{ margin: 0 }}>
              {templateOps.getElementCaption(elementItem)}
            </Card.Text>
            <Row className="d-flex justify-content-end">
              <Button
                variant="outlined"
                color="primary"
                title="Fullscreen"
                onClick={() => setOpenFullScreen(true)}
                style={{ minWidth: '38px', height: '38px', padding: 0 }}
              >
                <FontAwesomeIcon icon="expand" />
              </Button>
            </Row>
          </Card.Body>
        )}
        <OpenModalFromUrl
          isFullScreen
          isImage
          openModal={openFullScreen}
          setOpenModal={setOpenFullScreen}
          url={templateOps.getElementValue(elementItem)}
          title={templateOps.getElementKey(elementItem)}
        />
      </Card>
    );

  if (previewMode === EDITION_MODE)
    return (
      <Form.Group className="mb-0">
        <Form.Label>Image</Form.Label>
        <Form.Control
          type="text"
          name={`${templateOps.getElementKey(elementItem)}-url`}
          placeholder="URL for the image"
          defaultValue={templateOps.getElementValue(elementItem)}
          onBlur={event => updateAnnotationFields(elementItem, 'value', event.target.value)}
        />
        <Form.Control
          type="text"
          name={`${templateOps.getElementKey(elementItem)}-caption`}
          placeholder="Caption for the image"
          defaultValue={templateOps.getElementCaption(elementItem)}
          onBlur={event => updateAnnotationFields(elementItem, 'caption', event.target.value)}
        />
        <Form.Control
          type="text"
          name={`${templateOps.getElementKey(elementItem)}-hiperLink`}
          placeholder="Hiperlink for image"
          defaultValue={templateOps.getElementHiperLink(elementItem)}
          onBlur={event => updateAnnotationFields(elementItem, 'hiperLink', event.target.value)}
        />
      </Form.Group>
    );

  return '';
};

ImageItem.defaultProps = {
  updateAnnotationFields: () => console.error('no action to update')
};

ImageItem.propTypes = {
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func,
  previewMode: PropTypes.string.isRequired
};

const RadioItem = ({ elementItem, updateAnnotationFields, previewMode }) => {
  const optionList = templateOps.getElementOptionList(elementItem);

  if (previewMode === PREVIEW_MODE && !isEmpty(templateOps.getElementValue(elementItem)))
    return (
      <Form.Group className="mb-0">
        <Form.Label>{templateOps.getElementLabelName(elementItem)}</Form.Label>
        <Form.Control value={templateOps.getElementValue(elementItem)} readOnly plaintext />
      </Form.Group>
    );

  if (previewMode === EDITION_MODE)
    return (
      <Form.Group className="mb-0">
        <Form.Label>{templateOps.getElementLabelName(elementItem)}</Form.Label>
        {optionList.map(option => (
          <Form.Check
            name={templateOps.getElementKey(elementItem)}
            key={option}
            type="radio"
            defaultChecked={option === templateOps.getElementValue(elementItem)}
            onChange={() => updateAnnotationFields(elementItem, 'value', option)}
            label={option}
          />
        ))}
      </Form.Group>
    );

  return '';
};

RadioItem.defaultProps = {
  updateAnnotationFields: () => console.error('no action to update')
};

RadioItem.propTypes = {
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func,
  previewMode: PropTypes.string.isRequired
};

const SelectItem = ({ elementItem, updateAnnotationFields, previewMode }) => {
  const optionList = templateOps.getElementOptionList(elementItem);

  if (previewMode === PREVIEW_MODE && !isEmpty(templateOps.getElementValue(elementItem)))
    return (
      <Form.Group className="mb-0">
        <Form.Label>{templateOps.getElementLabelName(elementItem)}</Form.Label>
        <Form.Control value={templateOps.getElementValue(elementItem)} readOnly plaintext />
      </Form.Group>
    );

  if (previewMode === EDITION_MODE)
    return (
      <Form.Group className="mb-0">
        <Form.Label>{templateOps.getElementLabelName(elementItem)}</Form.Label>
        <Form.Control
          as="select"
          name={templateOps.getElementKey(elementItem)}
          defaultValue={templateOps.getElementValue(elementItem)}
          onBlur={event => updateAnnotationFields(elementItem, 'value', event.target.value)}
        >
          <option value="" disabled hidden>
            Select option
          </option>
          {optionList.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Form.Control>
      </Form.Group>
    );

  return '';
};

SelectItem.defaultProps = {
  updateAnnotationFields: () => console.error('no action to update')
};

SelectItem.propTypes = {
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func,
  previewMode: PropTypes.string.isRequired
};

const InputItem = ({ elementItem, updateAnnotationFields, previewMode }) => {
  let placeholder = 'Enter text';
  if (templateOps.getElementLabelName(elementItem) === 'number') placeholder = 'Enter number';

  if (previewMode === PREVIEW_MODE && !isEmpty(templateOps.getElementValue(elementItem)))
    return (
      <Form.Group className="mb-0">
        <Form.Label>{templateOps.getElementLabelName(elementItem)}</Form.Label>
        <Form.Control value={templateOps.getElementValue(elementItem)} readOnly plaintext />
      </Form.Group>
    );

  if (previewMode === EDITION_MODE)
    return (
      <Form.Group className="mb-0">
        <Form.Label>{templateOps.getElementLabelName(elementItem)}</Form.Label>
        <Form.Control
          type={templateOps.getElementLabelName(elementItem)}
          name={templateOps.getElementKey(elementItem)}
          placeholder={placeholder}
          defaultValue={templateOps.getElementValue(elementItem)}
          onBlur={event => updateAnnotationFields(elementItem, 'value', event.target.value)}
        />
      </Form.Group>
    );

  return '';
};

InputItem.defaultProps = {
  updateAnnotationFields: () => console.error('no action to update')
};

InputItem.propTypes = {
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func,
  previewMode: PropTypes.string.isRequired
};

const TemplateItem = ({ annotationTemplate, rowNumber, columnNumber, itemNumber, ...rest }) => {
  const elementItem = templateOps.getElementItem({
    annotationTemplateObject: annotationTemplate,
    rowNumber,
    columnNumber,
    itemNumber
  });
  if (isNil(elementItem)) return '';

  switch (elementItem.elementType) {
    case ELEMENT_TYPES.INPUT:
      return <InputItem elementItem={elementItem} {...rest} />;
    case ELEMENT_TYPES.SELECT:
      return <SelectItem elementItem={elementItem} {...rest} />;
    case ELEMENT_TYPES.RADIO:
      return <RadioItem elementItem={elementItem} {...rest} />;
    case ELEMENT_TYPES.IMAGE:
      return <ImageItem elementItem={elementItem} {...rest} />;
    case ELEMENT_TYPES.VIDEO:
      return <VideoItem elementItem={elementItem} {...rest} />;
    case ELEMENT_TYPES.LINK:
      return <LinkItem elementItem={elementItem} {...rest} />;
    case ELEMENT_TYPES.ICON:
      return <IconItem elementItem={elementItem} {...rest} />;
    case ELEMENT_TYPES.TEXTAREA:
      return <TextAreaItem elementItem={elementItem} {...rest} />;
    case ELEMENT_TYPES.IFRAME:
      return <IframeItem elementItem={elementItem} {...rest} />;
    case ELEMENT_TYPES.IMAPS:
      return <ImapsItem elementItem={elementItem} {...rest} />;
    case ELEMENT_TYPES.POINTCLOUD:
      return <PointCloudItem elementItem={elementItem} {...rest} />;
    case ELEMENT_TYPES.BUTTON:
      return <ButtonItem elementItem={elementItem} {...rest} />;
    case ELEMENT_TYPES.MULTIPLEIMAPS:
      return <MultipleMapsItem elementItem={elementItem} {...rest} />;
    default:
      return '';
  }
};

TemplateItem.propTypes = {
  annotationTemplate: PropTypes.object.isRequired,
  rowNumber: PropTypes.number.isRequired,
  columnNumber: PropTypes.number.isRequired,
  itemNumber: PropTypes.number.isRequired
};

const TemplateCol = ({ annotationTemplate, rowNumber, columnNumber, ...rest }) => {
  const totalItemsInColumn = templateOps.getItemsInColumn({
    annotationTemplateObject: annotationTemplate,
    rowNumber,
    columnNumber
  });
  const itemsToRender = [];
  for (let itemNumber = 1; itemNumber <= totalItemsInColumn; itemNumber += 1) {
    itemsToRender.push(
      <TemplateItem
        key={`${templateOps.getName(annotationTemplate)}-element-${itemNumber}`}
        rowNumber={rowNumber}
        annotationTemplate={annotationTemplate}
        columnNumber={columnNumber}
        itemNumber={itemNumber}
        {...rest}
      />
    );
  }
  return itemsToRender;
};

TemplateCol.propTypes = {
  annotationTemplate: PropTypes.object.isRequired,
  rowNumber: PropTypes.number.isRequired,
  columnNumber: PropTypes.number.isRequired
};

const TemplateRow = ({ annotationTemplate, rowNumber, ...rest }) => {
  const totalColumns = templateOps.getColumnsForRow({
    annotationTemplateObject: annotationTemplate,
    rowNumber
  });
  const columnWidth = Math.floor(12 / totalColumns);
  const columnsToRender = [];
  for (let columnNumber = 1; columnNumber <= totalColumns; columnNumber += 1) {
    columnsToRender.push(
      <Col
        xs={columnWidth}
        key={`${templateOps.getName(annotationTemplate)}-column-${columnNumber}`}
      >
        <TemplateCol
          rowNumber={rowNumber}
          annotationTemplate={annotationTemplate}
          columnNumber={columnNumber}
          {...rest}
        />
      </Col>
    );
  }
  return (
    <>
      <Form.Row>{columnsToRender}</Form.Row>
      <ColBreak />
    </>
  );
};

TemplateRow.propTypes = {
  annotationTemplate: PropTypes.object.isRequired,
  rowNumber: PropTypes.number.isRequired
};

const AnnotationTemplatePreview = ({ annotationTemplate, ...rest }) => {
  const totalRows = templateOps.getRows(annotationTemplate);
  const rowsToRender = [];

  for (let rowNumber = 1; rowNumber <= totalRows; rowNumber += 1) {
    rowsToRender.push(
      <TemplateRow
        key={`${templateOps.getName(annotationTemplate)}-row-${rowNumber}`}
        rowNumber={rowNumber}
        annotationTemplate={annotationTemplate}
        {...rest}
      />
    );
  }
  return (
    <form id="videoEditorForm" onSubmit={event => event.preventDefault()}>
      {rowsToRender}
    </form>
  );
};

AnnotationTemplatePreview.propTypes = {
  annotationTemplate: PropTypes.object.isRequired,
  previewMode: PropTypes.string.isRequired
};

export default AnnotationTemplatePreview;
